import React, { useState, useEffect, useRef } from "react";
import ButtonList from "../components/ButtonList";
// import Logo from "../assets/isuzu_spin_logo_shine.mp4";
// import Logo from "../assets/isuzu_truck_logo.png";
import Logo from "../assets/2dlogo.mp4";
import Cursor from "../assets/pointer.png";
import { useNavigate } from "react-router-dom";

const Idle = ({ videoData = [] }) => {
  const [videoClicks, setVideoClicks] = useState([]);
  const [videoClickTimes, setVideoClickTimes] = useState({});
  const [cursorPosition, setCursorPosition] = useState({ left: "75%", top: "120%" });
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now());
  const [isCursorAnimating, setIsCursorAnimating] = useState(false);
  const [activeClickIndex, setActiveClickIndex] = useState(null);
  const gridPositions = useRef([]);
  const navigate = useNavigate();
  const cursorRef = useRef(null);
  // Add refs to store the timeout IDs so we can cancel them if needed
  const clickTimeoutRef = useRef(null);
  const navigateTimeoutRef = useRef(null);
  // Add a ref to track animation state to avoid closure issues
  const animatingRef = useRef(false);

  // Format timestamp to Oceania time (AEST/AEDT)
  const formatTimestamp = (timestamp) => {
    const options = {
      timeZone: 'Australia/Sydney', // Using Sydney as reference for Oceania time
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };

    return new Date(timestamp).toLocaleString('en-AU', options);
  };

  // Calculate grid positions for cursor animation targets
  useEffect(() => {
    const calculatePositions = () => {
      const positions = [];
      for (let i = 1; i <= 8; i++) {
        const gridItem = document.querySelector(`.grid-item.position-${i}`);
        if (gridItem) {
          const rect = gridItem.getBoundingClientRect();
          positions.push({
            index: i - 1,
            left: `${rect.left + rect.width / 1.8}px`,
            top: `${rect.top + rect.height / 1.5}px`
          });
        }
      }
      gridPositions.current = positions;
    };

    // Calculate once on mount
    calculatePositions();

    // Recalculate on window resize
    window.addEventListener('resize', calculatePositions);

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(calculatePositions, 500);

    return () => {
      window.removeEventListener('resize', calculatePositions);
      clearTimeout(timer);
    };
  }, [videoData]);

  // Initialize video click counts and timestamps
  useEffect(() => {
    if (videoData.length > 0) {
      // Initialize click counts
      const storedCounts = localStorage.getItem("videoClickCounts");
      if (storedCounts) {
        try {
          const parsed = JSON.parse(storedCounts);
          if (Array.isArray(parsed) && parsed.length === videoData.length) {
            setVideoClicks(parsed);
          } else {
            setVideoClicks(Array(videoData.length).fill(0));
          }
        } catch (err) {
          console.error("Failed to parse videoClickCounts:", err);
          setVideoClicks(Array(videoData.length).fill(0));
        }
      } else {
        setVideoClicks(Array(videoData.length).fill(0));
      }

      // Initialize click timestamps
      const storedTimes = localStorage.getItem("videoClickTimes");
      if (storedTimes) {
        try {
          const parsedTimes = JSON.parse(storedTimes);
          setVideoClickTimes(parsedTimes);
        } catch (err) {
          console.error("Failed to parse videoClickTimes:", err);
          const initialTimestamps = {};
          videoData.forEach((_, index) => {
            initialTimestamps[index] = [];
          });
          setVideoClickTimes(initialTimestamps);
        }
      } else {
        const initialTimestamps = {};
        videoData.forEach((_, index) => {
          initialTimestamps[index] = [];
        });
        setVideoClickTimes(initialTimestamps);
      }
    }
  }, [videoData]);

  // Save to localStorage when videoClicks or videoClickTimes changes
  useEffect(() => {
    if (videoClicks.length === videoData.length) {
      localStorage.setItem("videoClickCounts", JSON.stringify(videoClicks));
    }
  }, [videoClicks, videoData.length]);

  useEffect(() => {
    if (Object.keys(videoClickTimes).length > 0) {
      localStorage.setItem("videoClickTimes", JSON.stringify(videoClickTimes));
    }
  }, [videoClickTimes]);

  // Keep the ref updated with the current state
  useEffect(() => {
    animatingRef.current = isCursorAnimating;
  }, [isCursorAnimating]);

  // Function to cancel any pending animations and clicks
  const cancelPendingAnimation = () => {
    if (animatingRef.current) {
      // Clear any pending timeouts
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }

      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current);
        navigateTimeoutRef.current = null;
      }

      // Reset cursor position
      setCursorPosition({ left: "75%", top: "120%" });
      setIsCursorAnimating(false);
      animatingRef.current = false;
      setActiveClickIndex(null);

      console.log("Animation cancelled due to user interaction");
    }
  };

  // Track user interaction
  useEffect(() => {
    const updateLastInteraction = () => {
      setLastInteractionTime(Date.now());
      // Cancel any pending animations when user interacts
      cancelPendingAnimation();
    };

    // Add event listeners for all types of interaction
    window.addEventListener("mousemove", updateLastInteraction);
    window.addEventListener("click", updateLastInteraction);
    window.addEventListener("touchstart", updateLastInteraction);
    window.addEventListener("keydown", updateLastInteraction);
    window.addEventListener("scroll", updateLastInteraction);

    // Check for inactivity every second
    const inactivityTimer = setInterval(() => {
      if (Date.now() - lastInteractionTime > 3000 && !animatingRef.current && gridPositions.current.length > 0) {
        // User has been inactive for 3 seconds
        animateCursorToRandomPosition();
      }
    }, 1000);

    return () => {
      // Clean up event listeners and timer
      window.removeEventListener("mousemove", updateLastInteraction);
      window.removeEventListener("click", updateLastInteraction);
      window.removeEventListener("touchstart", updateLastInteraction);
      window.removeEventListener("keydown", updateLastInteraction);
      window.removeEventListener("scroll", updateLastInteraction);
      clearInterval(inactivityTimer);

      // Also clear any pending timeouts on unmount
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current);
      }
    };
  }, [lastInteractionTime]);

  // Animate cursor to a random position
  const animateCursorToRandomPosition = () => {
    if (gridPositions.current.length === 0 || animatingRef.current) return;

    console.log("Starting cursor animation");
    setIsCursorAnimating(true);
    animatingRef.current = true;

    // Select a random grid position
    const randomIndex = Math.floor(Math.random() * gridPositions.current.length);
    const targetPosition = gridPositions.current[randomIndex];

    console.log("Moving cursor to position:", targetPosition);

    // Set cursor position to target position
    setCursorPosition({
      left: targetPosition.left,
      top: targetPosition.top
    });

    // Store the targetIndex for use in the timeout
    const targetIndex = targetPosition.index;

    // Store the timeout ID in the ref so we can cancel it if needed
    clickTimeoutRef.current = setTimeout(() => {
      console.log("Timeout executed, animating state:", animatingRef.current);
      // Only trigger if still animating (not cancelled by user interaction)
      if (animatingRef.current) {
        console.log("Triggering video select for index:", targetIndex);
        triggerAutomaticVideoSelect(targetIndex);
      } else {
        console.log("Animation was cancelled, not triggering video select");
      }
    }, 1500); // Wait for animation to complete (1.5s transition)
  };

  // Trigger automatic video selection
  const triggerAutomaticVideoSelect = (index) => {
    console.log("Triggering automatic video selection for index:", index);
    // Set active click index to show the click animation
    setActiveClickIndex(index);

    // Navigate to the video after animation completes
    navigateTimeoutRef.current = setTimeout(() => {
      console.log("Navigating to video:", index + 1);
      setIsCursorAnimating(false);
      animatingRef.current = false;
      setActiveClickIndex(null);
      navigate(`/povvideo/${index + 1}`);
    }, 500);
  };

  // Save click data to a file
  const saveClickDataToFile = async () => {
    // Create a new array structure for timestamps in a more readable format
    const formattedClickData = [];

    // For each video, create an entry with title, click count and formatted timestamps
    videoData.forEach((video, index) => {
      const clickInfo = {
        videoTitle: video.title,
        clickCount: videoClicks[index] || 0,
        timestamps: (videoClickTimes[index] || []).map(timestamp => formatTimestamp(timestamp))
      };
      formattedClickData.push(clickInfo);
    });

    const data = {
      summary: `Total clicks by video (exported on ${formatTimestamp(new Date().toISOString())})`,
      clickData: formattedClickData
    };

    const content = JSON.stringify(data, null, 2);
    const blob = new Blob([content], { type: 'application/json' });

    // Try to use File System Access API if available
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'video-click-data.json',
          types: [{
            description: 'JSON File',
            accept: { 'application/json': ['.json'] },
          }],
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        console.log("Data saved to file successfully");
      } catch (err) {
        console.error("Error saving file:", err);
        // Fall back to download if user cancels or there's an error
        downloadFile(blob, 'video-click-data.json');
      }
    } else {
      // Fall back to download for browsers that don't support File System Access API
      downloadFile(blob, 'video-click-data.json');
    }
  };

  // Helper function to download file
  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Normal user-initiated video click
  const handleVideoClick = (index) => {
    setActiveClickIndex(index);

    // Update click count
    const updated = [...videoClicks];
    updated[index] += 1;
    setVideoClicks(updated);

    // Record timestamp
    const now = new Date().toISOString();

    // Update timestamps - store just the ISO string
    setVideoClickTimes(prev => {
      const newTimes = { ...prev };
      if (!newTimes[index]) {
        newTimes[index] = [];
      }
      newTimes[index].push(now);
      return newTimes;
    });

    // Save both to localStorage
    localStorage.setItem("videoClickCounts", JSON.stringify(updated));
    console.log("Click counters:", updated);

    // Auto-save data to file after each click
    // saveClickDataToFile();

    setTimeout(() => {
      navigate(`/povvideo/${index + 1}`);
      setActiveClickIndex(null);
    }, 500);
  };

  const resetCounters = () => {
    const reset = Array(videoData.length).fill(0);
    setVideoClicks(reset);

    // Also reset timestamps
    const resetTimes = {};
    videoData.forEach((_, index) => {
      resetTimes[index] = [];
    });
    setVideoClickTimes(resetTimes);

    // Update localStorage
    localStorage.setItem("videoClickCounts", JSON.stringify(reset));
    localStorage.setItem("videoClickTimes", JSON.stringify(resetTimes));

    console.log("Click counters and timestamps reset");

    // Save empty data to file
    // saveClickDataToFile();
  };

  return (
    <div className="idle-outer-frame">
      <div className="video-grid-container">
        <div className="position-absolute d-flex justify-content-center align-items-center">
          <button className="reset btn" onClick={resetCounters}>
            <video
              src={Logo}
              className="logo"
              autoPlay
              loop
              muted
            />
          </button>
        </div>

        {/* Cursor element positioned absolutely and explicitly at highest z-index */}
        <img
          ref={cursorRef}
          src={Cursor}
          alt="Cursor"
          className="cursor"
          style={{
            position: "fixed",
            left: cursorPosition.left,
            top: cursorPosition.top,
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            pointerEvents: "none",
            transition: "left 1.5s ease-in-out, top 1.5s ease-in-out"
          }}
        />

        {/* Data export button */}
        <button
          onClick={saveClickDataToFile}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 100,
            padding: '10px 15px',
            background: '#b82a11',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Export Click Data
        </button>

{/* <div style={{
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 50,
  fontFamily: "'Shorai Sans', sans-serif",
  fontSize: '32px',
}}>
  いすゞ - ISUZU
</div>

<div style={{
  position: 'absolute',
  top: '20%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 50,
  fontFamily: "'Arial', sans-serif",
  fontSize: '32px',
}}>
  いすゞ - ISUZU
</div> */}


        <ButtonList
          items={videoData.map((videoTitlePair) => videoTitlePair.title)}
          imageUrls={videoData.map(item => item.image || null)}
          screen={"idle"}
          layout="grid"
          onVideoClick={handleVideoClick}
          activeClickIndex={activeClickIndex}
        />
      </div>
    </div>
  );
};

export default Idle;